import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { isAuthenticated, createDocument as createDocumentApi } from "../../ServiceRequest";

function generateDocumentProperties(templateAlias: string, projectId: string, projectTitle: string, brand:string, brandSub: string) {
  const now = new Date();
const formattedDate = now.toLocaleString("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

  return {
    name: `${templateAlias} Letter - ${projectId} ${brandSub} ${projectTitle}`,
    description: `${templateAlias} Letter for project ${projectTitle} (#${projectId}), ${brand}. Created on ${formattedDate}` };
}

const fieldRequired = (v?: string | null) => !!v && v.trim().length > 0;

export default function DocumentCreateConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const documentId = params.get("documentId") || "";
  const projectId = params.get("projectId") || "";
  const templateName = params.get("templateName") || "";
  const templateAlias = params.get("templateAlias") || "";
  const projectName = params.get("projectName") || "";
  const projectSub = params.get("projectSub") || "";
  const projectBrand = params.get("brand") || "";

  
  const defaults = useMemo(() => generateDocumentProperties(templateAlias, projectId, projectName, projectBrand, projectSub), []);
  const [name, setName] = useState<string>(defaults.name);
  const [description, setDescription] = useState<string>(defaults.description);
  const [submitting, setSubmitting] = useState(false);

  const canCreate =
    fieldRequired(name) && fieldRequired(projectId) && fieldRequired(templateName);

  const onCreate = async () => {
    if (!canCreate) return;

    if (isAuthenticated && !isAuthenticated()) {
      toast({ status: "warning", title: "You need to be authenticated." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await createDocumentApi(
        name,
        description,
        templateName,
        projectId,
        documentId || undefined 
      );

      if (res?.success && res?.data?._id) {
        toast({
          status: "success",
          title: "Document created",
          description: `New ID: ${res.data._id}`,
        });
        navigate(`/${projectId}/${res.data._id}/${templateName}`, { replace: true });
      } else {
        toast({
          status: "error",
          title: "Creation failed",
          description: "Unexpected server response.",
        });
      }
    } catch {
      toast({
        status: "error",
        title: "Creation failed",
        description: "Please check your inputs or try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = () => navigate("/", { replace: true });

  return (
    <Container maxW="2xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Heading size="lg">Create document</Heading>

        <Text>
          The document  for  <strong>{templateAlias || "(unspecified template)"}</strong>{" "}
          {projectId && <> (Project # <strong>{projectId}</strong>)</>} does not exist.
          Would you like to create it now?
        </Text>

        {!documentId && (
          <Alert status="warning">
            <AlertIcon />
            No <code>documentId</code> provided in the query string. A new document will be
            created and the backend will assign its <code>_id</code>.
          </Alert>
        )}

        <Box as="form" onSubmit={(e: any) => e.preventDefault()}>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Document name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name"
                maxLength={120}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description || ""}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                rows={3}
                maxLength={500}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Template</FormLabel>
              <Input
                value={templateAlias}
                readOnly={true}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Project ID</FormLabel>
              <Input
                value={projectId}
                readOnly={true}
              />
            </FormControl>
          </VStack>
        </Box>

        <HStack justify="flex-end" spacing={3}>
          <button className="el-btn el-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="el-btn"
            onClick={onCreate}
          >
            Create
          </button>
        </HStack>

      </VStack>
    </Container>
  );
}
